/**
 * Pure utilities for introspecting AI SDK v6 UIMessage[] shape.
 * Used by the chat UI to decide what to render in the bottom slot and
 * which placeholder hint to show in the input.
 */

import type { UIMessage } from "ai";

export interface QuickReplyItem {
  label: string;
  value: string;
}

export interface LatestAssistantSignals {
  quickReplies: QuickReplyItem[] | null;
  inputPlaceholder: string | null;
}

const EMPTY_SIGNALS: LatestAssistantSignals = {
  quickReplies: null,
  inputPlaceholder: null,
};

interface ToolOutputPart {
  type: string;
  state: string;
  output?: {
    success?: boolean;
    quickReplies?: QuickReplyItem[];
    placeholder?: string;
  };
}

export function isToolPart(part: { type: string }): boolean {
  return part.type.startsWith("tool-") || part.type === "dynamic-tool";
}

/**
 * Single backward scan of `messages` that extracts both the latest assistant
 * quick-reply chips and the latest input-placeholder hint — whichever the
 * most recent assistant turn emitted. Either field is null when absent.
 *
 * Within the winning assistant message the last matching tool invocation
 * wins (if the LLM calls the same tool twice in one turn).
 */
export function getLatestAssistantSignals(messages: UIMessage[]): LatestAssistantSignals {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg.role !== "assistant") continue;

    const signals: LatestAssistantSignals = {
      quickReplies: null,
      inputPlaceholder: null,
    };
    for (const part of msg.parts) {
      if (!isToolPart(part)) continue;
      const toolPart = part as unknown as ToolOutputPart;
      if (toolPart.state !== "output-available" || !toolPart.output?.success) {
        continue;
      }
      if (Array.isArray(toolPart.output.quickReplies)) {
        signals.quickReplies = toolPart.output.quickReplies;
      }
      if (typeof toolPart.output.placeholder === "string") {
        signals.inputPlaceholder = toolPart.output.placeholder;
      }
    }
    return signals;
  }
  return EMPTY_SIGNALS;
}

/** True when the user has sent at least one message (non-empty conversation). */
export function hasUserTurn(messages: UIMessage[]): boolean {
  return messages.some((m) => m.role === "user");
}

/**
 * The text of the last user turn, or undefined when there is none.
 *
 * Accepts the RAW request body's `messages`, not a validated `UIMessage[]`:
 * the two shapes the AI SDK transport can send both appear here (v6 `parts`,
 * and the older `content` string), and the route has to read one before it has
 * converted anything.
 *
 * Extracted so the pre-model INSERT and the `onFinish` persistence read the
 * user's question the same way. When those diverged, the row written before
 * the model call and the row written after it could disagree about what the
 * user asked.
 */
export function extractLastUserText(messages: unknown): string | undefined {
  if (!Array.isArray(messages)) return undefined;
  const last = messages[messages.length - 1] as
    | { role?: string; content?: unknown; parts?: unknown }
    | undefined;
  if (!last || last.role !== "user") return undefined;

  if (Array.isArray(last.parts)) {
    const text = (last.parts as { type?: string; text?: string }[])
      .filter((p) => p.type === "text")
      .map((p) => p.text ?? "")
      .join("");
    return text.length > 0 ? text : undefined;
  }
  if (typeof last.content === "string") {
    return last.content.length > 0 ? last.content : undefined;
  }
  return JSON.stringify(last);
}
